import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconTrendUp, IconCheckCircle, FEATURE_ICONS } from './LandingIcons';
import { PRODUCT_FEATURES } from '../data/productFeatures';
import './Landing.css';

export default function Landing() {
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const showcaseRef = useRef(null);
  const go = (path) => { setMobileNavOpen(false); navigate(path); };
  const moveShowcase = (direction) => {
    const track = showcaseRef.current;
    const card = track?.querySelector('.lp-story-card');
    if (!track || !card) return;
    const gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap) || 0;
    track.scrollBy({ left: direction * (card.getBoundingClientRect().width + gap), behavior: 'smooth' });
  };
  return (
    <div className="lp">
      {/* Nav */}
      <header className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-logo">
            <img className="lp-logo-mark" src="/app-icon.png" alt="" />
            <span className="lp-logo-text">TuWay Africa</span>
          </div>
          <nav className={`lp-nav-links${mobileNavOpen ? ' mobile-open' : ''}`}>
            <details className="lp-products">
              <summary>Products <span className="lp-nav-chevron" aria-hidden="true" /></summary>
              <div className="lp-products-menu">
                {PRODUCT_FEATURES.map(product => (
                  <a href={`/products/${product.slug}`} key={product.title}
                    onClick={(e) => { e.preventDefault(); go(`/products/${product.slug}`); }}>
                    <span className="lp-product-icon" aria-hidden="true">{product.icon}</span>
                    <span>
                      <strong>{product.title}</strong>
                      <small>{product.navDesc}</small>
                    </span>
                  </a>
                ))}
              </div>
            </details>
            <a href="#features" onClick={() => setMobileNavOpen(false)}>What you get</a>
            <a href="#how" onClick={() => setMobileNavOpen(false)}>How it works</a>
            <a href="#pricing" onClick={() => setMobileNavOpen(false)}>Pricing</a>
            <details className="lp-resources">
              <summary>Resources <span className="lp-nav-chevron" aria-hidden="true" /></summary>
              <div className="lp-resources-menu">
                <a href="/faq" onClick={(e) => { e.preventDefault(); go('/faq'); }}>
                  <span className="lp-resources-icon" aria-hidden="true">?</span>
                  <span>
                    <strong>Help &amp; FAQ</strong>
                    <small>Answers and feature guides</small>
                  </span>
                </a>
              </div>
            </details>
            <div className="lp-mobile-actions">
              <button className="lp-btn lp-btn-ghost" onClick={() => go('/login')}>Sign in</button>
              <button className="lp-btn lp-btn-primary" onClick={() => go('/register')}>Get started free</button>
            </div>
          </nav>
          <button
            className="lp-nav-toggle"
            onClick={() => setMobileNavOpen(open => !open)}
            aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileNavOpen}
          >
            <span className={`lp-hamburger${mobileNavOpen ? ' open' : ''}`} aria-hidden="true"><span /><span /><span /></span>
          </button>
          <div className="lp-nav-actions">
            <button className="lp-btn lp-btn-ghost" onClick={() => navigate('/login')}>Sign in</button>
            <button className="lp-btn lp-btn-primary" onClick={() => navigate('/register')}>Get started free</button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="lp-hero">
        <img className="lp-hero-backdrop" src="/hero-cinematic.png" alt="A creative entrepreneur growing her business with TuWay Africa" />
        <div className="lp-hero-shade" />
        <div className="lp-hero-copy">
          <div className="lp-badge"><span className="lp-badge-dot" /> Customer growth, without the guesswork</div>
          <h1 className="lp-hero-title">
            Move your<br />audience.<br /><span className="lp-accent">Move forward.</span>
          </h1>
          <p className="lp-hero-sub">
            Reach your whole customer list, handle every reply together and see exactly
            what moves people—from first message to repeat business.
          </p>
          <div className="lp-hero-actions">
            <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={() => navigate('/register')}>
              Start for free <span className="lp-arrow">→</span>
            </button>
            <button className="lp-btn lp-btn-outline lp-btn-lg" onClick={() => navigate('/login')}>
              Sign in
            </button>
          </div>
          <div className="lp-hero-trust">
            <span>✓ No credit card required</span>
            <span>✓ Free plan to start</span>
            <span>✓ Running the same day</span>
          </div>
        </div>
        <div className="lp-hero-signal" aria-hidden="true">
          <span className="lp-signal-avatar">R</span>
          <span><strong>Your customers are listening.</strong><small>2,481 messages delivered</small></span>
          <b>✓✓</b>
        </div>
        <div className="lp-scroll-cue" aria-hidden="true"><span>Discover</span><i /></div>
      </section>

      {/* Product story */}
      <section className="lp-product-story">
        <div className="lp-product-story-copy">
          <span className="lp-eyebrow">The full picture</span>
          <h2>Every customer.<br /><span>One continuous story.</span></h2>
          <p>From the first hello to the next sale, TuWay keeps the signal clear—who replied, what worked and where your momentum is coming from.</p>
          <button className="lp-btn lp-btn-outline" onClick={() => navigate('/register')}>Explore the platform <span>→</span></button>
        </div>
        <div className="lp-hero-visual lp-story-visual">
          <div className="lp-preview">
            <div className="lp-preview-bar">
              <span className="lp-dot lp-dot-r" />
              <span className="lp-dot lp-dot-y" />
              <span className="lp-dot lp-dot-g" />
              <span className="lp-preview-url">app.tuwayafrica.com</span>
            </div>
            <div className="lp-preview-body">
              <div className="lp-preview-stats">
                {PREVIEW_STATS.map(s => (
                  <div className="lp-pstat" key={s.label}>
                    <div className="lp-pstat-num">{s.num}</div>
                    <div className="lp-pstat-label">{s.label}</div>
                  </div>
                ))}
              </div>
              <div className="lp-preview-chart">
                {[42, 68, 55, 80, 72, 95, 88].map((h, i) => (
                  <div className="lp-bar" key={i} style={{ height: `${h}%`, animationDelay: `${i * 0.08}s` }} />
                ))}
              </div>
              <div className="lp-preview-msg">
                <div className="lp-msg-avatar">R</div>
                <div className="lp-msg-bubble">
                  <span>Your album drops Friday! Tap to pre-save…</span>
                  <span className="lp-msg-meta">Delivered · Read ✓✓</span>
                </div>
              </div>
            </div>
          </div>
          <div className="lp-float lp-float-1"><IconTrendUp /> 32% clicked</div>
          <div className="lp-float lp-float-2"><IconCheckCircle /> 2,481 delivered</div>
        </div>
      </section>

      {/* Horizontal product stories */}
      <section className="lp-horizontal" aria-labelledby="lp-horizontal-title">
        <div className="lp-horizontal-head">
          <div>
            <span className="lp-eyebrow">Built to keep moving</span>
            <h2 id="lp-horizontal-title">One platform.<br /><span>Every move.</span></h2>
          </div>
          <div className="lp-horizontal-controls" aria-label="Scroll product stories">
            <button type="button" onClick={() => moveShowcase(-1)} aria-label="Previous product story">←</button>
            <button type="button" onClick={() => moveShowcase(1)} aria-label="Next product story">→</button>
          </div>
        </div>

        <div className="lp-horizontal-track" ref={showcaseRef} tabIndex="0" aria-label="TuWay product stories">
          {SHOWCASES.map((item, index) => (
            <article className={`lp-story-card lp-story-${item.kind}`} key={item.title}>
              <div className="lp-story-media">
                <img className="lp-story-image" src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" />
                <div className="lp-story-number">0{index + 1}</div>
                <StoryArtwork item={item} />
              </div>
              <div className="lp-story-card-copy">
                <span>{item.kicker}</span>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            </article>
          ))}
          <article className="lp-story-card lp-story-cta">
            <div className="lp-cta-arrow" aria-hidden="true">↗</div>
            <div className="lp-story-card-copy">
              <span>Your next move</span>
              <h3>Put your audience within reach.</h3>
              <button className="lp-btn lp-btn-primary" onClick={() => navigate('/register')}>Start for free →</button>
            </div>
          </article>
        </div>

        <div className="lp-motion-ticker" aria-hidden="true">
          <div>
            <span>MOVE WITH TUWAY</span><i>•</i><span>KEEP MOVING</span><i>•</i><span>MOVE WITH TUWAY</span><i>•</i><span>KEEP MOVING</span><i>•</i>
            <span>MOVE WITH TUWAY</span><i>•</i><span>KEEP MOVING</span><i>•</i><span>MOVE WITH TUWAY</span><i>•</i><span>KEEP MOVING</span><i>•</i>
          </div>
        </div>
      </section>

      {/* Audience spotlight */}
      <section className="lp-spotlight" aria-labelledby="lp-spotlight-title">
        <div className="lp-spotlight-copy">
          <div className="lp-spotlight-brand"><strong>TUWAY</strong><span>SPOTLIGHT</span></div>
          <h2 id="lp-spotlight-title">For people who<br /><span>move people.</span></h2>
          <p>Turn every launch, event and offer into a conversation. Reach your customers directly, learn what lands, and keep your biggest supporters close.</p>
          <p className="lp-spotlight-note">Your audience is already listening. Meet them where they are.</p>
          <button className="lp-btn lp-btn-primary lp-btn-lg" onClick={() => navigate('/register')}>Build your audience <span>→</span></button>
        </div>

        <div className="lp-spotlight-visual" aria-hidden="true">
          <div className="lp-spotlight-glow" />
          <div className="lp-spotlight-portrait">
            <img src="/audience-spotlight.png" alt="" loading="lazy" decoding="async" />
          </div>
          <span className="lp-orbit lp-orbit-1"><b>AM</b></span>
          <span className="lp-orbit lp-orbit-2"><b>RK</b></span>
          <span className="lp-orbit lp-orbit-3"><b>+2.4K</b></span>
          <span className="lp-orbit lp-orbit-4"><b>✓✓</b></span>
          <div className="lp-spotlight-message">
            <span className="lp-message-send">↗</span>
            <span><strong>Tonight at 7. You’re invited.</strong><small>Sent to your most engaged customers</small></span>
          </div>
        </div>
      </section>

      {/* Moving campaign image rail */}
      <section className="lp-image-rail" aria-label="The people and businesses moving with TuWay">
        <div className="lp-image-track">
          {[0, 1].map(group => (
            <div className="lp-image-group" key={group} aria-hidden={group === 1 ? 'true' : undefined}>
              {CAMPAIGN_IMAGES.map((image, index) => (
                <figure className={`lp-image-card lp-image-card-${index + 1}`} key={`${group}-${image.src}`}>
                  <img src={image.src} alt={group === 0 ? image.alt : ''} loading="lazy" decoding="async" />
                </figure>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Trust stats */}
      <section className="lp-metrics">
        {METRICS.map(m => (
          <div className="lp-metric" key={m.label}>
            <div className="lp-metric-num">{m.num}</div>
            <div className="lp-metric-label">{m.label}</div>
          </div>
        ))}
      </section>

      {/* Features */}
      <section className="lp-section" id="features">
        <div className="lp-section-head">
          <div className="lp-eyebrow">What you get</div>
          <h2 className="lp-section-title">One place to move your business forward.</h2>
          <p className="lp-section-sub">WhatsApp, email, customer records and reporting — in one place, for one price.</p>
        </div>
        <div className="lp-features">
          {FEATURES.map((f, i) => {
            const FeatureIcon = FEATURE_ICONS[i];
            return (
              <div className="lp-feature" key={i}>
                <div className="lp-feature-icon"><FeatureIcon /></div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="lp-section lp-how" id="how">
        <div className="lp-section-head">
          <div className="lp-eyebrow">How it works</div>
          <h2 className="lp-section-title">Up and running in minutes</h2>
        </div>
        <div className="lp-steps">
          {STEPS.map((s, i) => (
            <div className="lp-step" key={i}>
              <div className="lp-step-num">{i + 1}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="lp-section lp-pricing" id="pricing">
        <div className="lp-section-head">
          <div className="lp-eyebrow">Pricing</div>
          <h2 className="lp-section-title">Simple plans that scale with you</h2>
          <p className="lp-section-sub">Choose a monthly platform plan based on the size of your contact list.</p>
        </div>
        <div className="lp-plans">
          {PLANS.map(p => (
            <div className={'lp-plan' + (p.featured ? ' featured' : '')} key={p.name}>
              {p.featured && <div className="lp-plan-flag">Most popular</div>}
              <div className="lp-plan-name">{p.name}</div>
              <div className="lp-plan-price">
                {p.pricePrefix && <span className="lp-plan-prefix">{p.pricePrefix}</span>}
                <span className="lp-plan-amount">${p.price}</span>
                <span className="lp-plan-per">/mo</span>
              </div>
              <div className="lp-plan-tagline">{p.contacts}</div>
              <button
                className={'lp-btn lp-btn-lg ' + (p.featured ? 'lp-btn-primary' : 'lp-btn-outline')}
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => navigate('/register')}
              >
                {p.cta}
              </button>
              <ul className="lp-plan-features">
                {p.features.map((feat, i) => (
                  <li key={i}><span className="lp-check">✓</span> {feat}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="lp-pricing-note">
          Messages are sold as prepaid bundles at Meta's per-message cost plus a markup that
          decreases as volume increases. Enterprise pricing is quoted from $250 per month.
        </p>
      </section>

      {/* Testimonials */}
      <section className="lp-section">
        <div className="lp-section-head">
          <div className="lp-eyebrow">Testimonials</div>
          <h2 className="lp-section-title">Loved by creators &amp; businesses</h2>
        </div>
        <div className="lp-testimonials">
          {TESTIMONIALS.map((t, i) => (
            <div className="lp-testimonial" key={i}>
              <div className="lp-stars">★★★★★</div>
              <p className="lp-quote">"{t.text}"</p>
              <div className="lp-author">
                <div className="lp-author-avatar">{t.name[0]}</div>
                <div>
                  <div className="lp-author-name">{t.name}</div>
                  <div className="lp-author-role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="lp-cta">
        <div className="lp-cta-card">
          <h2>Ready to talk to every customer this week?</h2>
          <p>Join the businesses using TuWay Africa to stay close to their customers.</p>
          <button className="lp-btn lp-btn-white lp-btn-lg" onClick={() => navigate('/register')}>
            Get started — it's free <span className="lp-arrow">→</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <div className="lp-logo">
            <img className="lp-logo-mark" src="/app-icon.png" alt="" />
            <span className="lp-logo-text">TuWay Africa</span>
          </div>
          <div className="lp-footer-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>Sign in</a>
          </div>
          <p className="lp-footer-copy">© {new Date().getFullYear()} TuWay Africa. Built for artists &amp; SMEs.</p>
        </div>
      </footer>
    </div>
  );
}

function StoryArtwork({ item }) {
  if (item.kind === 'broadcast') {
    return <div className="lp-story-art broadcast-art" aria-hidden="true"><div className="art-message"><span>Summer collection</span><strong>Ready for something new?</strong><small>Delivered to 2,481 customers</small></div><div className="art-rings"><i /><i /><i /></div><b>{item.metric}</b></div>;
  }
  if (item.kind === 'inbox') {
    return <div className="lp-story-art inbox-art" aria-hidden="true"><div className="art-chat left">Is this still available?</div><div className="art-chat right">Yes—want me to reserve it?</div><div className="art-chat left short">Perfect ✓</div><b>{item.metric}</b></div>;
  }
  if (item.kind === 'automation') {
    return <div className="lp-story-art automation-art" aria-hidden="true"><div className="flow-node main">NEW CUSTOMER</div><i /><div className="flow-row"><span>WELCOME</span><span>FOLLOW UP</span></div><b>{item.metric}</b></div>;
  }
  if (item.kind === 'analytics') {
    return <div className="lp-story-art analytics-art" aria-hidden="true"><div className="art-bars">{[36, 58, 44, 72, 66, 91, 82].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}</div><div className="art-curve" /><b>{item.metric}</b></div>;
  }
  return null;
}

const PREVIEW_STATS = [
  { num: '2,481', label: 'Sent' },
  { num: '98%', label: 'Delivered' },
  { num: '32%', label: 'Clicked' },
];

const METRICS = [
  { num: '10K+', label: 'Messages sent' },
  { num: '98%', label: 'Arrive first time' },
  { num: '5×', label: 'Faster than sending by hand' },
  { num: '24/7', label: 'Keeps working after hours' },
];

const CAMPAIGN_IMAGES = [
  { src: '/campaign-fashion.png', alt: 'Fashion founder in a sculptural green look' },
  { src: '/campaign-creative.png', alt: 'Creative founder in her colourful studio' },
  { src: '/campaign-founder.png', alt: 'Entrepreneur in a cinematic studio portrait' },
  { src: '/audience-spotlight.png', alt: 'Creative business owner lit in green and amber' },
  { src: '/hero-cinematic.png', alt: 'Entrepreneur connecting with her audience' },
];

const SHOWCASES = [
  { kind: 'broadcast', kicker: 'Broadcasts', title: 'Reach the room.', description: 'Launch one message across WhatsApp and email without losing the personal touch.', metric: '2,481 reached', image: '/story-broadcast.png', imageAlt: 'Creative entrepreneur ready to reach her audience' },
  { kind: 'inbox', kicker: 'Shared inbox', title: 'Stay in the moment.', description: 'Every reply, every customer and every teammate in one continuous conversation.', metric: '4× faster replies', image: '/story-community.png', imageAlt: 'Creative community sharing a conversation' },
  { kind: 'automation', kicker: 'Automations', title: 'Keep showing up.', description: 'Welcome, follow up and reconnect while your team focuses on the conversations that need a human.', metric: '24/7 momentum', image: '/story-automation.png', imageAlt: 'Product founder beside orders ready to move' },
  { kind: 'analytics', kicker: 'Live analytics', title: 'Know what moved.', description: 'See what arrived, what was read and what earned the next click—while it is happening.', metric: '+32% clicks', image: '/story-analytics.png', imageAlt: 'Creative director considering his next move' },
];

const FEATURES = [
  { title: 'One message, your whole list', desc: 'Reach thousands of customers on WhatsApp or email at once — or just the group you choose.' },
  { title: 'Every customer in one place', desc: 'Names, numbers, history and preferences your whole team can see. Bring your existing list in from a spreadsheet.' },
  { title: 'See what people click', desc: 'Share a link and find out how many customers opened it, so you know what works before you spend more.' },
  { title: 'Results in plain numbers', desc: 'How many messages arrived, how many were read, how many led to a click — without building a single spreadsheet.' },
  { title: 'Follow-ups that run themselves', desc: 'Set the reply once and every customer who asks gets it — evenings, weekends and holidays included.' },
  { title: 'Say it once, reuse it forever', desc: 'Save the messages you send often so anyone on the team can send them, worded exactly the same way.' },
];

const STEPS = [
  { title: 'Connect WhatsApp', desc: 'Link your business WhatsApp number. A few minutes, once.' },
  { title: 'Bring in your customers', desc: 'Upload the list you already have, or add people as they come in.' },
  { title: 'Send your first message', desc: 'Write it, choose who gets it, send — or schedule it for when your customers are most likely to read.' },
  { title: 'See what it earned you', desc: 'Watch what was delivered, read and clicked as it happens.' },
];

const PLANS = [
  {
    name: 'Starter',
    price: 12,
    contacts: 'Up to 1,000 contacts',
    cta: 'Get started',
    features: [
      'Your numbers at a glance',
      'One shared customer list',
      'WhatsApp and email messaging',
      'Send to your whole list at once',
      'Nothing lost if your connection drops',
    ],
  },
  {
    name: 'Growth',
    price: 35,
    contacts: 'Up to 5,000 contacts',
    cta: 'Get started',
    featured: true,
    features: [
      'Everything in Starter',
      'One shared inbox for the whole team',
      'Full reporting on every message sent',
      'Links that show you who clicked',
      'Team schedules and live agent status',
    ],
  },
  {
    name: 'Pro',
    price: 95,
    contacts: 'Up to 15,000 contacts',
    cta: 'Get started',
    features: [
      'Everything in Growth',
      'Automatic replies and follow-ups',
      'Decide who on your team sees what',
      'Hands-on help when something looks wrong',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    price: 250,
    pricePrefix: 'From',
    contacts: '15,000+ contacts',
    cta: 'Contact sales',
    features: [
      'Everything in Pro',
      'Automatic replies and follow-ups',
      'Decide who on your team sees what',
      'Hands-on help when something looks wrong',
      'Priority support',
    ],
  },
];

// Placeholder quotes — swap these for real customer feedback before launch.
const TESTIMONIALS = [
  { text: 'Three times as many people read what we send now. The tracked links paid for the whole thing in the first week.', name: 'Tapiwa M.', role: 'Music Artist, Harare' },
  { text: 'We used to message customers one by one. Now the whole 2,000-person list hears from us in a minute.', name: 'Rudo C.', role: 'SME Owner, Bulawayo' },
  { text: 'New customers get a proper welcome without anyone lifting a finger. That used to be someone\'s whole morning.', name: 'Brian K.', role: 'Digital Agency, Nairobi' },
];
